import { Box, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { SettingsData } from '../hooks/use-settings-data.hook';
import { SettingsRowKey, SettingsSectionBaseProps } from '../types';
import { useSettingsStyles } from '../styles';
import _ from 'lodash';
import { DobForm, useDobForm } from './forms';
import { SexForm, useSexForm } from './forms/sex';
import { NameForm, useNameForm } from './forms/name';
import { EditableSectionContainer, EditableSectionRow, LoadableTypography } from '@uplift/ui-v2';
import { OptionalTypography } from '../components/optional-typography.component';
import { DateFormats } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';

const prepareBasicSettingsData = (data: SettingsData) => {

  const legalName = `${data?.user?.firstName} ${data?.user?.lastName}`;

  return {
    legalName: legalName,
    preferredName: data?.profile?.preferredName,
    dob: CalendarDateService.date(data?.profile?.dob).format(DateFormats.DEFAULT_DATE),
    sex: _.capitalize(data?.profile?.sex),
  };
};

export const BasicsSettingsSection: React.VFC<SettingsSectionBaseProps> = ({ userId, data, isLoading }) => {
  const l10n = useL10n();
  const { classes } = useSettingsStyles();
  // const credentials = (data?.practiceCredentials ?? []).map((practiceCredential) => practiceCredential.credential);
  const basicsData = prepareBasicSettingsData(data);
  const { dobFormik } = useDobForm(userId, data);
  const { nameFormik } = useNameForm(userId, data);
  const { sexFormik } = useSexForm(userId, data);

  return (
    <EditableSectionContainer
      title={l10n.providerSettings.basics}
      rowDividers
      formikConfigs={{
        [SettingsRowKey.name]: nameFormik,
        [SettingsRowKey.dob]: dobFormik,
        [SettingsRowKey.sex]: sexFormik,
      }}
    >
      <EditableSectionRow
        label={l10n.providerSettings.name}
        rowKey={SettingsRowKey.name}
        form={<NameForm mentalHealthRoles={data?.mentalHealthRoles} />}
        kind="editable-row"
      >
        <Box display="flex">
          <Box flex={1}>
            <Typography color="textSecondary">{l10n.providerSettings.legal}</Typography>
            <LoadableTypography
              className={classes.fontWeight500}
              loading={isLoading}
              width="100px"
            >
              {basicsData?.legalName}
            </LoadableTypography>
          </Box>
          <Box flex={1}>
            <Typography color="textSecondary">{l10n.providerSettings.preferred}</Typography>
            <LoadableTypography
              className={classes.fontWeight500}
              loading={isLoading}
              width="100px"
              component={OptionalTypography}
              loadUndefinedChildren
            >
              {basicsData?.preferredName}
            </LoadableTypography>
          </Box>
        </Box>
      </EditableSectionRow>

      <EditableSectionRow
        label={l10n.providerSettings.dateOfBirth}
        rowKey={SettingsRowKey.dob}
        form={<DobForm />}
        kind="editable-row"
        rowFormWrapperProps={{
          mobileModalTitle: l10n.providerSettings.editDateOfBirth
        }}
      >
        <LoadableTypography
          className={classes.fontWeight500}
          loading={isLoading}
          width="100px"
        >
          {basicsData?.dob}
        </LoadableTypography>
      </EditableSectionRow>

      <EditableSectionRow
        label={l10n.providerSettings.sex}
        rowKey={SettingsRowKey.sex}
        form={<SexForm />}
        kind="editable-row"
      >
        <LoadableTypography
          className={classes.fontWeight500}
          loading={isLoading}
          width="60px"
        >
          {basicsData?.sex}
        </LoadableTypography>
      </EditableSectionRow>
    </EditableSectionContainer>
  );
};
